/**
 * Builds the Excel/Word/PDF export for the Related Parties register.
 *
 * The on-screen table is the source of truth — not `summarizationTasks.output`,
 * which is a counts-and-narrative dump. The export is the unfiltered register:
 * one row per active party, including excluded, matching the table columns.
 */

import type { ExportContent, ExportSheet } from "@/utils/export";
import type { RelatedPartyEntryUI } from "./types";

const PARTY_TYPE_LABELS: Record<string, string> = {
  individual: "Individual",
  company: "Company",
  trust: "Trust",
};

const STATUS_LABELS: Record<string, string> = {
  confirmed: "Confirmed",
  probable: "Probable",
  possible: "Possible",
  excluded: "Excluded",
};

const CATEGORY_LABELS: Record<string, string> = {
  director: "Director",
  former_director: "Former director",
  shareholder: "Shareholder",
  related_body_corporate: "Related body corporate",
  director_relative: "Director relative",
  spouse_of_director: "Spouse of director",
  controlled_entity: "Controlled entity",
  associate: "Associate",
  unknown_associated_party: "Unknown associated party",
  spouse: "Spouse (Bankruptcy Act s 5)",
  de_facto_partner: "De facto partner (s 5)",
  former_spouse: "Former spouse (s 5)",
  former_de_facto_partner: "Former de facto partner (s 5)",
  relative: "Relative (s 5)",
};

const STATUS_SORT: Record<string, number> = {
  confirmed: 0,
  probable: 1,
  possible: 2,
  excluded: 3,
};

function relatedPartiesSheet(entries: RelatedPartyEntryUI[]): ExportSheet {
  const sorted = [...entries]
    .filter((e) => e.status === "active")
    .sort((a, b) => {
      const byStatus =
        (STATUS_SORT[a.relationshipStatus] ?? 9) -
        (STATUS_SORT[b.relationshipStatus] ?? 9);
      if (byStatus !== 0) return byStatus;
      return a.legalName.localeCompare(b.legalName);
    });

  return {
    name: "Related Parties",
    headers: ["Name", "Type", "Summary", "Status", "Category"],
    rows: sorted.map((party) => [
      party.legalName,
      PARTY_TYPE_LABELS[party.partyType] ?? party.partyType,
      party.summary ?? "",
      STATUS_LABELS[party.relationshipStatus] ?? party.relationshipStatus,
      CATEGORY_LABELS[party.relationshipCategory] ?? party.relationshipCategory,
    ]),
  };
}

export function buildRelatedPartiesExport(
  entries: RelatedPartyEntryUI[],
): ExportContent {
  return {
    title: "Related Parties",
    sheets: [relatedPartiesSheet(entries)],
  };
}
